/**
 * kioskAuthSlice.ts
 *
 * Dedicated Redux slice for kiosk authentication state.
 * This is intentionally kept separate from commonSlice, which is shared
 * with the main application, to prevent cross-contamination of auth state.
 *
 * Source of truth hierarchy:
 *   Backend session (HttpOnly cookie) → this slice → route guards / UI
 *
 * localStorage is NOT used for authentication decisions in the kiosk app.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';

// ─── Interfaces ─────────────────────────────────────────────────────────────

export interface KioskUser {
    userEmail: string;
    userBusinessEmail: string;
    userId: number;
    firstName: string;
    lastName: string;
    picPath: string;
}

export interface KioskCompanyDetails {
    companyName?: string;
    companyDomain?: string;
    app_name?: string;
    companyLogoPath?: string;
}

export interface KioskAuthState {
    /**
     * true  → session verification is in-flight; route guards must not render.
     * false → session check is complete; guards can make routing decisions.
     *
     * Starts as true so the very first render is always a loading state,
     * never an incorrect unauthenticated redirect.
     */
    isAuthChecking: boolean;

    /** Whether the user has an active, server-verified session. */
    isLoggedIn: boolean;

    /** Whether the user has accepted the kiosk consent. */
    isConsentProvided: boolean;

    /** Whether the user's phone number has been verified. */
    isPhoneVerified: boolean;

    /** Hydrated user object from the server. Null when not authenticated. */
    user: KioskUser | null;

    /** Branding details hydrated from the server. Null if not fetched yet. */
    companyDetails: KioskCompanyDetails | null;

    /** Transient invite code for the session. */
    inviteCode: string | null;
}

// ─── Initial State ───────────────────────────────────────────────────────────

const initialState: KioskAuthState = {
    isAuthChecking: true,   // Block routes until the first session check completes
    isLoggedIn: false,
    isConsentProvided: false,
    isPhoneVerified: false,
    user: null,
    companyDetails: null,
    inviteCode: typeof window !== 'undefined' ? sessionStorage.getItem('kioskInviteCode') : null,
};

// ─── Slice ───────────────────────────────────────────────────────────────────

export const kioskAuthSlice = createSlice({
    name: 'kioskAuthSlice',
    initialState,
    reducers: {
        /**
         * Toggle the initialization loading gate.
         * Set to false once the session verification API call has resolved
         * (success or failure).
         */
        setAuthChecking: (state, action: PayloadAction<boolean>) => {
            state.isAuthChecking = action.payload;
        },

        /**
         * Hydrate the slice with verified server data after a successful
         * session check or after a successful login/external-auth callback.
         */
        setKioskSession: (
            state,
            action: PayloadAction<{
                user: KioskUser;
            }>
        ) => {
            state.isLoggedIn = true;
            state.user = action.payload.user;
        },

        /**
         * Wipe all auth state — called on logout, session expiry, or a
         * failed session verification.
         */
        clearKioskSession: (state) => {
            state.isLoggedIn = false;
            state.isConsentProvided = false;
            state.isPhoneVerified = false;
            state.user = null;
        },

        /**
         * Convenience updater for kiosk-specific flags without touching the
         * user object. Useful after consent acceptance or phone verification.
         */
        updateKioskFlags: (
            state,
            action: PayloadAction<{
                isConsentProvided?: boolean;
                isPhoneVerified?: boolean;
            }>
        ) => {
            if (action.payload.isConsentProvided !== undefined) {
                state.isConsentProvided = action.payload.isConsentProvided;
            }
            if (action.payload.isPhoneVerified !== undefined) {
                state.isPhoneVerified = action.payload.isPhoneVerified;
            }
        },

        /**
         * Store company branding details.
         */
        setKioskCompanyDetails: (
            state,
            action: PayloadAction<KioskCompanyDetails | null>
        ) => {
            state.companyDetails = action.payload;
        },

        /**
         * Store kiosk invite code (transient).
         */
        setKioskInviteCode: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.inviteCode = action.payload;
            if (typeof window !== 'undefined') {
                if (action.payload) {
                    sessionStorage.setItem('kioskInviteCode', action.payload);
                } else {
                    sessionStorage.removeItem('kioskInviteCode');
                }
            }
        },
    },
});

export const {
    setAuthChecking,
    setKioskSession,
    clearKioskSession,
    updateKioskFlags,
    setKioskCompanyDetails,
    setKioskInviteCode,
} = kioskAuthSlice.actions;

export const selectKioskIsLoggedIn = (state: RootState) => state.kioskAuthSlice.isLoggedIn;
export const selectKioskIsPhoneVerified = (state: RootState) => state.kioskAuthSlice.isPhoneVerified;
export const selectKioskIsConsentProvided = (state: RootState) => state.kioskAuthSlice.isConsentProvided;
export const selectKioskIsAuthChecking = (state: RootState) => state.kioskAuthSlice.isAuthChecking;
export const selectKioskUser = (state: RootState) => state.kioskAuthSlice.user;
export const selectKioskCompanyDetails = (state: RootState) => state.kioskAuthSlice.companyDetails;
export const selectKioskInviteCode = (state: RootState) => state.kioskAuthSlice.inviteCode;

